import type { Listing } from "@/data/listings";
import type { BlogPost } from "@/api/blogs";

import type { PublicSeoSerpTemplate } from "@/api/seo";

export type SsrGeneratedGuideContent = {
  pageKey: string;
  pageType: "city" | "subject" | "citySubject";
  citySlug: string;
  subjectSlug: string;
  cityName: string;
  subjectName: string;
  status: "generating" | "ready" | "failed";
  title: string;
  intro: string;
  excerpt: string;
  contentMarkdown: string;
  faq: Array<{ question: string; answer: string }>;
  cta: {
    label: string;
    href: string;
    description: string;
  };
};

export type SsrRouteType =
  | "none"
  | "blogPost"
  | "pressReleasePost"
  | "courseSingle"
  | "courseCitySubject"
  | "courseTutor"
  | "tuteurs"
  | "blogIndex"
  | "home";

export type SsrBlogPostData = {
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  galleryImageUrls?: string[];
  authorName?: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
  /** Révision éditoriale réelle ; alimente `dateModified`. */
  lastReviewedAt?: string;
};

export type SsrPressReleaseData = {
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  authorName?: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
  tags?: string[];
};

export type SsrTutorSeoData = {
  title: string;
  city: string;
  subject: string;
  tutorSlug: string;
  description?: string;
  ownerName?: string;
  ownerAvatarUrl?: string;
  rating?: number;
  reviews?: number;
};

export type SsrRouteData = {
  routeType: SsrRouteType;
  notFound?: boolean;
  statusCode?: number;
  // Uniquement les gabarits SERP qui diffèrent des valeurs par défaut
  // embarquées dans le bundle (voir backend/src/ssr/routeData.js).
  seoSerpTemplates?: Record<string, PublicSeoSerpTemplate>;
  blogPost?: SsrBlogPostData;
  pressReleasePost?: SsrPressReleaseData;
  courseSingle?: {
    slug: string;
    cityName?: string;
    subjectName?: string;
    isKnown: boolean;
    /** Annonces actives, comptées au rendu serveur (décide du noindex). */
    listingCount?: number;
    /**
     * Villes voisines, résolues au rendu serveur, pour que le maillage interne
     * existe dans le HTML au lieu d'apparaître après hydratation.
     */
    featuredCities?: string[];
    /**
     * Ville figurant parmi les plus grandes de France : indexable même sans
     * annonce, la demande y étant mesurée et l'offre devant y arriver en
     * premier. Ne concerne que les pages villes, jamais ville × matière.
     */
    isMajorCity?: boolean;
    generatedContent?: SsrGeneratedGuideContent;
  };
  courseCitySubject?: {
    citySlug: string;
    subjectSlug: string;
    cityName?: string;
    subjectName?: string;
    isKnown: boolean;
    /** Annonces actives, comptées au rendu serveur (décide du noindex). */
    listingCount?: number;
    /** Villes voisines, résolues au rendu serveur (ancrées sur la ville). */
    featuredCities?: string[];
    generatedContent?: SsrGeneratedGuideContent;
  };
  courseTutor?: SsrTutorSeoData;
  /**
   * Première page de /tuteurs, rendue côté serveur.
   *
   * `items` est volontairement typé `Listing[]` : la charge utile est produite
   * par le même sérialiseur que `GET /api/listings`.
   */
  tuteurs?: {
    items: Listing[];
    total: number;
    page: number;
    limit: number;
  };
  /**
   * Index du blog rendu côté serveur.
   *
   * `content` est absent de la charge utile (89 Ko cumulés pour 9 articles) ;
   * `wordCount` le remplace pour le calcul du temps de lecture.
   */
  blogIndex?: SsrBlogIndex;
  /** Page d'accueil : uniquement la liste d'articles, le reste a déjà un repli. */
  home?: { blog: SsrBlogIndex };
};

export type SsrBlogIndexItem = BlogPost & { wordCount?: number };

export type SsrBlogIndex = {
  items: SsrBlogIndexItem[];
  total: number;
  page: number;
  limit: number;
};

export const DEFAULT_SSR_ROUTE_DATA: SsrRouteData = {
  routeType: "none",
};
